import { Fade } from "@chakra-ui/react";
import { useState } from "react";
import { useMap } from "react-map-gl/maplibre";

import { MapDeckGl } from "@nens/deck-gl";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { CommentIcon, RanaLoading } from "@nens/ranalib";
import { createMapLibreTransformFunction } from "@nens/react-map-gl/utils";

import { RanaPopup } from "@/lib/rana-popup/rana-popup";

import { FIT_BOUNDS_OPTIONS } from "../consts/fit-bounds-options";
import { useRanaMap } from "../hooks/use-rana-map";
import { RanaMapProps } from "../types/rana-map-props-types";

import { Rana3DBuildingsLayer } from "./rana-3d-buildings-layer";
import { RanaMapControls } from "./rana-map-controls";
import { RanaMapEvents } from "./rana-map-events";

type RanaMapSelfProps = RanaMapProps & ReturnType<typeof useRanaMap>;

export function RanaMapSelf({
  mapId,
  mapStyle,
  isThreeD,
  setThreeD,
  setIsThreeD,
  zoomIn,
  zoomOut,
  toFlat,
  show3DBuildings,
  children,
  hideExport,
  containerProps,
  commentProps,
  isLoading,
  dontApplyInitialBounds,
  bounds,
  sprites,
  ...rest
}: RanaMapSelfProps) {
  const { [mapId]: map } = useMap();
  const [isHovering, setIsHovering] = useState(false);

  // Calculate screen positions for comment popups
  const selectedThreadLocation = commentProps?.selectedCommentThread?.location;

  if (!mapStyle) return null;

  // If the map is not yet initialized and isLoading is true and bounds are not known, show a loading spinner overlay
  // This prevents the map from rendering and then zooming in to the defined bounds, which can be visually jarring.
  if (!map && isLoading && !bounds) {
    return (
      <RanaMapControls.Overlay pointerEvents="none">
        <RanaLoading size="xl" />
      </RanaMapControls.Overlay>
    );
  }

  return (
    <MapDeckGl
      {...rest}
      id={mapId}
      mapProps={{
        mapStyle,
        attributionControl: false,
        initialViewState: {
          bounds: bounds ?? undefined,
          fitBoundsOptions: FIT_BOUNDS_OPTIONS,
        },
        transformRequest: createMapLibreTransformFunction(getRanaApiV1().accessToken + "", false),
        ...rest.mapProps,
        onClick: (e) => {
          // If hovering on deck.gl layer (hovering also means that layer is selectable) prevent other onClicks
          if (isHovering) return;

          if (commentProps) {
            // If thread selected just close it on map click
            if (commentProps.selectedCommentThread) return commentProps.onCloseCommentThread();

            // If creating comment just close it on map click
            if (commentProps.creatingCommentAt) return commentProps.onCloseCreatingCommentAt();

            // Handle comment placement mode
            if (commentProps.isAddingComment) return commentProps.addComment(e);
          }

          // Call the custom onClick handler if it exists
          if (rest.mapProps.onClick) rest.mapProps.onClick(e);
        },
        onMove: (e) => {
          // Call the custom onMove handler if it exists
          if (rest.mapProps.onMove) rest.mapProps.onMove(e);

          // Enable/disable 3D buildings based on pitch
          setIsThreeD(!!e.viewState.pitch);
        },
      }}
      deckGlProps={
        // If there are no deckGlProps, don't pass any props to deck.gl to avoid making a WebGl context for nothing, to improve performance and prevent hitting the WebGL context limit in the browser
        Object.keys(rest.deckGlProps ?? {}).length
          ? {
              pickingRadius: 5,
              getCursor: ({ isHovering }: { isHovering: boolean }) => {
                setIsHovering(isHovering);
                let cursorStyle = "";
                if (isHovering) {
                  cursorStyle = "pointer";
                } else if (commentProps?.isAddingComment) {
                  // Show comment icon cursor when in comment placement mode
                  cursorStyle = `url('${CommentIcon}'), auto`;
                }
                if (map) map.getCanvas().style.cursor = cursorStyle;
                return cursorStyle;
              },
              ...rest.deckGlProps,
            }
          : undefined
      }
    >
      <RanaMapEvents dontApplyInitialBounds={dontApplyInitialBounds} bounds={bounds} />

      <Fade in={isLoading} unmountOnExit>
        <RanaMapControls.Overlay pointerEvents="none">
          <RanaLoading size="xl" />
        </RanaMapControls.Overlay>
      </Fade>

      {/* Existing comment thread popup */}
      {selectedThreadLocation && commentProps?.selectedCommentThread && (
        <RanaPopup
          onClose={commentProps.onCloseCommentThread}
          point={selectedThreadLocation.coordinates as [number, number]}
          header={null}
          anchor="bottom"
          offset={25}
          hideCloseButton
        >
          {commentProps.renderCommentThread?.(commentProps.selectedCommentThread)}
        </RanaPopup>
      )}

      {/* New comment form popup */}
      {commentProps?.creatingCommentAt && commentProps?.creatingCommentAt && (
        <RanaPopup
          point={commentProps.creatingCommentAt}
          onClose={commentProps.onCloseCreatingCommentAt}
          header={null}
          anchor="bottom"
          offset={25}
        >
          {commentProps.renderNewCommentForm?.()}
        </RanaPopup>
      )}

      <Rana3DBuildingsLayer visible={show3DBuildings} />

      {children}
    </MapDeckGl>
  );
}
